import React from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { NotificationProvider } from './contexts/NotificationContext';
import { RenderProgressProvider } from './contexts/RenderProgressContext';
import AssetDetails from './pages/AssetDetails';
import BreakEvenAnalysis from './pages/BreakEvenAnalysis';
import NotificationPreferences from './pages/NotificationPreferences';
import Assets from './pages/Assets';
import AssignQRCode from './pages/AssignQRCode';
import Categories from './pages/Categories';
import CategoryFeatures from './pages/CategoryFeatures';
import CreateEditTaskTemplate from './pages/CreateEditTaskTemplate';
import CreateOrder from './pages/CreateOrder';
import Dashboard from './pages/Dashboard';
import EditJob from './pages/EditJob';
import EditOrder from './pages/EditOrder';
import EditQuote from './pages/EditQuote';
import EditShipment from './pages/EditShipment';
import EditTask from './pages/EditTask';
import JobDetails from './pages/JobDetails';
import Jobs from './pages/Jobs';
import Locations from './pages/Locations';
import Login from './pages/Login';
import OrderDetails from './pages/OrderDetails';
import Orders from './pages/Orders';
import QRCodeDetails from './pages/QRCodeDetails';
import QRCodes from './pages/QRCodes';
import QuoteDetails from './pages/QuoteDetails';
import QuotePublicView from './pages/QuotePublicView';
import Quotes from './pages/Quotes';
import Restyle from './pages/Restyle';
import Register from './pages/Register';
import ChangePassword from './pages/ChangePassword';
import ResetPassword from './pages/ResetPassword';
import RoomTypes from './pages/RoomTypes';
import ShipmentDetails from './pages/ShipmentDetails';
import Shipments from './pages/Shipments';
import ShopifySync from './pages/ShopifySync';
import SupercycleSubscriptionPush from './pages/SupercycleSubscriptionPush';
import SupplierDetails from './pages/SupplierDetails';
import Suppliers from './pages/Suppliers';
import TaskDetails from './pages/TaskDetails';
import Tasks from './pages/Tasks';
import TaskTemplates from './pages/TaskTemplates';
import UnitDetails from './pages/UnitDetails';
import Users from './pages/Users';
import Reservations from './pages/Reservations';
import ReservationNew from './pages/ReservationNew';
import ReservationDetails from './pages/ReservationDetails';
import { isAdmin, isReservationOnly, readStoredUser } from './lib/roles';

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (u?.must_change_password && !window.location.pathname.startsWith('/change-password')) {
      return <Navigate to="/change-password" replace />;
    }
    if (!isAdmin(u)) return <Navigate to="/dashboard" replace />;
  } catch {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

// Protected Route component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

interface ProtectedRouteOptions {
  requirePasswordChange?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps & ProtectedRouteOptions> = ({ children, requirePasswordChange = true }) => {
  const token = localStorage.getItem('token');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (requirePasswordChange) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user?.must_change_password && !window.location.pathname.startsWith('/change-password')) {
        return <Navigate to="/change-password" replace />;
      }
    } catch {}
  }

  const path = window.location.pathname;
  if (isReservationOnly(readStoredUser())) {
    const allowed = path.startsWith('/reservations')
      || path.startsWith('/assets')
      || path.startsWith('/units')
      || path.startsWith('/change-password');
    if (!allowed) return <Navigate to="/reservations" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <RenderProgressProvider>
      <NotificationProvider>
      <Toaster position="top-right" richColors closeButton />
      <Router>
        <div className="App">
          <Routes>
          {/* Redirect root based on authentication */}
          <Route path="/" element={
            localStorage.getItem('token') ?
            <Navigate to="/dashboard" replace /> :
            <Navigate to="/login" replace />
          } />

          {/* Authentication Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          } />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets/pre-loved"
            element={
              <ProtectedRoute>
                <Assets conditionFilter="pre_loved" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets/new"
            element={
              <ProtectedRoute>
                <Assets conditionFilter="not_pre_loved" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets"
            element={
              <ProtectedRoute>
                <Assets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets/:templateId"
            element={
              <ProtectedRoute>
                <AssetDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/units/:unitId"
            element={
              <ProtectedRoute>
                <UnitDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations"
            element={
              <ProtectedRoute>
                <Reservations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations/new"
            element={
              <ProtectedRoute>
                <ReservationNew />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations/:id"
            element={
              <ProtectedRoute>
                <ReservationDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <Categories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories/:categoryId/features"
            element={
              <ProtectedRoute>
                <CategoryFeatures />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ProtectedRoute>
                <Suppliers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers/:id"
            element={
              <ProtectedRoute>
                <SupplierDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/break-even"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <BreakEvenAnalysis />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/shipments"
            element={
              <ProtectedRoute>
                <Shipments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shipments/new"
            element={
              <ProtectedRoute>
                <EditShipment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shipments/:id/edit"
            element={
              <ProtectedRoute>
                <EditShipment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shipments/:id"
            element={
              <ProtectedRoute>
                <ShipmentDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <Jobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:id/edit"
            element={
              <ProtectedRoute>
                <EditJob />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:id"
            element={
              <ProtectedRoute>
                <JobDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <Orders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/create"
            element={
              <ProtectedRoute>
                <CreateOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id/edit"
            element={
              <ProtectedRoute>
                <EditOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <ProtectedRoute>
                <OrderDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <Tasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/:id/edit"
            element={
              <ProtectedRoute>
                <EditTask />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/:id"
            element={
              <ProtectedRoute>
                <TaskDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/task-templates"
            element={
              <ProtectedRoute>
                <TaskTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/task-templates/create"
            element={
              <ProtectedRoute>
                <CreateEditTaskTemplate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/task-templates/:id/edit"
            element={
              <ProtectedRoute>
                <CreateEditTaskTemplate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route path="/settings" element={<Navigate to="/settings/locations" replace />} />
          <Route
            path="/settings/locations"
            element={
              <ProtectedRoute>
                <Locations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/shopify-sync"
            element={
              <ProtectedRoute>
                <ShopifySync />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/supercycle-subscription-push"
            element={
              <ProtectedRoute>
                <SupercycleSubscriptionPush />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/room-types"
            element={
              <ProtectedRoute>
                <RoomTypes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <NotificationPreferences />
              </ProtectedRoute>
            }
          />
          <Route
            path="/qr-codes"
            element={
              <ProtectedRoute>
                <QRCodes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/qr-codes/:id"
            element={
              <ProtectedRoute>
                <QRCodeDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/qr-codes/:id/assign"
            element={
              <ProtectedRoute>
                <AssignQRCode />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <Quotes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/restyle"
            element={
              <ProtectedRoute>
                <Restyle />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/create"
            element={
              <ProtectedRoute>
                <EditQuote />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:id/edit"
            element={
              <ProtectedRoute>
                <EditQuote />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <ProtectedRoute>
                <QuoteDetails />
              </ProtectedRoute>
            }
          />

          {/* Public Routes (no auth required) */}
          <Route path="/quotes/view/:token" element={<QuotePublicView />} />

          {/* Catch all route - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </Router>
      </NotificationProvider>
    </RenderProgressProvider>
  );
}

export default App;
